import { cn } from "@bednbody/ui/lib/utils";
import type { ReactNode } from "react";

export interface PageHeaderProps {
  /** The page's name. A real h1, so the document has exactly one heading. */
  title: ReactNode;
  /** What the page is for, and who it is scoped to. Optional. */
  description?: ReactNode;
  /**
   * Whatever sits opposite the title: one or two controls, a status marker, a
   * badge. Deliberately free-form, because pages carry buttons, selects,
   * steppers and status dots, and a typed shape would fit almost none of them.
   * The caller keeps its own colours and sizes — this only owns the layout.
   */
  actions?: ReactNode;
  className?: string;
}

/**
 * The one page header: title, description, actions.
 *
 * Size and leading come from the shared typography tokens rather than Tailwind
 * size classes, so the scale survives a change of font. Admin currently remaps
 * both families to Geist, which makes these size-and-leading only; they resolve
 * to Nunito again if that experiment is reverted, with no code change.
 */
export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <div className={cn("flex flex-wrap items-start justify-between gap-4", className)}>
      {/* min-w-0 so a long description yields space to the actions instead of
          pushing them off the row. */}
      <div className="min-w-0">
        <h1 className="type-title-section">{title}</h1>
        {description != null ? (
          <p className="type-body-large max-w-prose text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions != null ? (
        <div className="flex flex-wrap items-start gap-2">{actions}</div>
      ) : null}
    </div>
  );
}